import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Plane,
  Briefcase,
  PartyPopper,
  Calendar,
  Plus,
  X,
  AlertTriangle,
  Check,
  ChevronRight,
  Edit3,
  Luggage,
  Trash2,
  Wand2,
  Search,
  Sparkles,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Badge,
  Field,
  LoadingState,
  ErrorState,
  Modal,
  FitThumb,
  Spinner,
  inputClass,
} from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import api from '@/lib/api';
import { PLAN_TYPES, STATUS_LABELS } from '@/lib/constants';
import { formatDate, parseFlexibleDate, todayISO, cn } from '@/lib/utils';
import type { FitPlan, PackingItem, PlanDay, PlannedFit, PlanType } from '@/lib/types';
import { SelectFitModal } from './components/SelectFitModal';
import { FitPhotoLightbox } from './components/FitPhotoLightbox';

const TYPE_ICONS: Record<PlanType, typeof Calendar> = {
  vacation: Plane,
  business: Briefcase,
  event: PartyPopper,
  other: Calendar,
};

function dayHeading(iso: string): { weekday: string; date: string } {
  const d = parseFlexibleDate(iso);
  return {
    weekday: d.toLocaleDateString('en-US', { weekday: 'long' }),
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  };
}

export function PlannerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApi<{ plan: FitPlan }>(id ? `/plans/${id}` : null);
  const { mutate, loading: mutating } = useMutation();

  const [selectSlot, setSelectSlot] = useState<PlannedFit | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PlannedFit | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [addingDate, setAddingDate] = useState<string | null>(null);
  const [lightboxFitId, setLightboxFitId] = useState<string | null>(null);

  const plan = data?.plan;
  const days: PlanDay[] = plan?.days ?? [];
  const today = todayISO();

  const totalSlots = days.reduce((acc, d) => acc + d.fits.length, 0);
  const filledSlots = days.reduce((acc, d) => acc + d.fits.filter((f) => f.fitId).length, 0);
  const warningCount = days.reduce((acc, d) => acc + d.fits.filter((f) => f.hasWarning).length, 0);

  // Trip context shared with the AI actions (pick from library, fit builder).
  const tripLine = plan
    ? `Trip: ${plan.name} (${plan.planType}), ${formatDate(plan.startDate)} to ${formatDate(plan.endDate)}.`
    : '';

  const handleAddSlot = async (date: string) => {
    if (!id) return;
    setAddingDate(date);
    try {
      await api.post(`/plans/${id}/fits`, { date });
      refetch();
    } finally {
      setAddingDate(null);
    }
  };

  const handleAssignFit = async (planFitId: string, fitId: string | null) => {
    if (!id) return;
    await api.patch(`/plans/${id}/fits/${planFitId}`, { fitId });
    refetch();
  };

  const handleRemove = async () => {
    if (!id || !removeTarget) return;
    await mutate('delete', `/plans/${id}/fits/${removeTarget.planFitId}`);
    setRemoveTarget(null);
    refetch();
  };

  const handleConfirm = async (planFitId: string) => {
    if (!id) return;
    setConfirmingId(planFitId);
    try {
      await api.patch(`/plans/${id}/fits/${planFitId}`, { confirmWorn: true });
      refetch();
    } finally {
      setConfirmingId(null);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    await mutate('delete', `/plans/${id}`);
    navigate('/fits/planner');
  };

  const handleSaveDayTitle = async (date: string, title: string) => {
    if (!id) return;
    await api.patch(`/plans/${id}/days/${date}`, { title });
  };

  const handleSaveCaption = async (planFitId: string, caption: string) => {
    if (!id) return;
    await api.patch(`/plans/${id}/fits/${planFitId}`, { caption });
  };

  // Ask the AI to pick a fit from the existing library for this slot.
  // Returns the model's reasoning so the row can surface it.
  const handleAiPick = async (slot: PlannedFit, date: string, caption: string): Promise<string> => {
    if (!id) return '';
    const res = await api.post<{ fitId: string | null; fitName: string | null; reason: string }>(
      '/ai/pick-fit',
      { planId: id, date, caption },
    );
    if (res?.fitId) {
      await api.patch(`/plans/${id}/fits/${slot.planFitId}`, { fitId: res.fitId });
      refetch();
    }
    return res?.reason ?? '';
  };

  // Spin up a fit builder session seeded with the trip details + caption as
  // the AI Stylist brief, linked back to this slot so the built fit lands here.
  const handleSendToStylist = async (planFitId: string, sessionName: string, blurb: string) => {
    if (!id) return;
    const res = await api.post<{ session: { id: string } }>('/fit-sessions', {
      name: sessionName,
      state: { context: blurb, plan: { planId: id, planFitId } },
    });
    if (res?.session?.id) navigate(`/fits/builder?session=${res.session.id}`);
  };

  return (
    <Page>
      <PageHeader section={plan?.name ?? 'Plan'} backHref="/fits/planner" backLabel="Planner" />
      <Main className="space-y-5">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {plan && (
          <>
            <PlanSummary plan={plan} totalSlots={totalSlots} filledSlots={filledSlots} warningCount={warningCount} />

            <div className="flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setShowEdit(true)}>
                <Edit3 className="w-4 h-4" />
                Edit Plan
              </Button>
              <Button variant="destructive" onClick={() => setShowDelete(true)} className="px-4">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Planned Outfits</h3>
              {days.map((day) => (
                <DayCard
                  key={day.date}
                  day={day}
                  planName={plan.name}
                  tripLine={tripLine}
                  today={today}
                  addingSlot={addingDate === day.date}
                  onAddSlot={handleAddSlot}
                  onSelectFit={setSelectSlot}
                  onClearFit={(slot) => handleAssignFit(slot.planFitId, null)}
                  onRemoveSlot={setRemoveTarget}
                  onConfirm={handleConfirm}
                  confirmingId={confirmingId}
                  onSaveDayTitle={handleSaveDayTitle}
                  onSaveCaption={handleSaveCaption}
                  onAiPick={handleAiPick}
                  onSendToStylist={handleSendToStylist}
                  onExpandPhoto={(fitId) => setLightboxFitId(fitId)}
                />
              ))}
            </section>

            {(plan.packing?.length ?? 0) > 0 && <PackingList items={plan.packing!} />}
          </>
        )}
      </Main>

      <FitPhotoLightbox fitId={lightboxFitId} onClose={() => setLightboxFitId(null)} />

      <SelectFitModal
        isOpen={selectSlot !== null}
        onClose={() => setSelectSlot(null)}
        onSelect={async (fitId) => {
          if (selectSlot) await handleAssignFit(selectSlot.planFitId, fitId);
        }}
      />

      {plan && (
        <EditPlanModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          plan={plan}
          onSaved={() => {
            setShowEdit(false);
            refetch();
          }}
        />
      )}

      <Modal
        isOpen={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title="Remove Slot?"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setRemoveTarget(null)} disabled={mutating}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth onClick={handleRemove} disabled={mutating}>
              {mutating ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          Remove {removeTarget?.fitName ? `“${removeTarget.fitName}”` : 'this fit slot'}
          {removeTarget?.caption ? ` (“${removeTarget.caption}”)` : ''} from this day?
        </p>
      </Modal>

      <Modal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete Plan?"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowDelete(false)} disabled={mutating}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth onClick={handleDelete} disabled={mutating}>
              {mutating ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          This will permanently delete {plan ? `“${plan.name}”` : 'this plan'} and all its scheduled fits.
        </p>
      </Modal>
    </Page>
  );
}

function PlanSummary({
  plan,
  totalSlots,
  filledSlots,
  warningCount,
}: {
  plan: FitPlan;
  totalSlots: number;
  filledSlots: number;
  warningCount: number;
}) {
  const Icon = TYPE_ICONS[plan.planType] ?? Calendar;
  return (
    <Card className="p-4">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-secondary rounded-xl">
          <Icon className="w-6 h-6 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-foreground">{plan.name}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {formatDate(plan.startDate)} – {formatDate(plan.endDate)}
          </p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-muted-foreground">
              {totalSlots === 0
                ? 'No fits planned'
                : filledSlots === totalSlots
                  ? `${filledSlots} fit${filledSlots !== 1 ? 's' : ''} planned`
                  : `${filledSlots} of ${totalSlots} slots filled`}
            </span>
            {warningCount > 0 && (
              <Badge tone="warning">
                {warningCount} warning{warningCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function DayCard({
  day,
  planName,
  tripLine,
  today,
  addingSlot,
  onAddSlot,
  onSelectFit,
  onClearFit,
  onRemoveSlot,
  onConfirm,
  confirmingId,
  onSaveDayTitle,
  onSaveCaption,
  onAiPick,
  onSendToStylist,
  onExpandPhoto,
}: {
  day: PlanDay;
  planName: string;
  tripLine: string;
  today: string;
  addingSlot: boolean;
  onAddSlot: (date: string) => void | Promise<void>;
  onSelectFit: (slot: PlannedFit) => void;
  onClearFit: (slot: PlannedFit) => void | Promise<void>;
  onRemoveSlot: (slot: PlannedFit) => void;
  onConfirm: (planFitId: string) => void;
  confirmingId: string | null;
  onSaveDayTitle: (date: string, title: string) => void | Promise<void>;
  onSaveCaption: (planFitId: string, caption: string) => void | Promise<void>;
  onAiPick: (slot: PlannedFit, date: string, caption: string) => Promise<string>;
  onSendToStylist: (planFitId: string, sessionName: string, blurb: string) => void | Promise<void>;
  onExpandPhoto: (fitId: string) => void;
}) {
  const isPast = day.date < today;
  const isToday = day.date === today;
  const { weekday, date } = dayHeading(day.date);
  const [title, setTitle] = useState(day.title ?? '');
  const dayLabel = title.trim() || `${weekday}, ${date}`;
  const dayLine = `Day: ${dayLabel}${title.trim() ? ` (${weekday}, ${date})` : ''}.`;

  const saveTitle = () => {
    if ((day.title ?? '') !== title) onSaveDayTitle(day.date, title.trim());
  };

  return (
    <Card className={cn('p-4 space-y-3', isPast && 'opacity-75')}>
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-foreground">
              {weekday}, {date}
            </h4>
            {isToday && <Badge tone="accent">Today</Badge>}
            {isPast && !isToday && <Badge tone="neutral">Past</Badge>}
          </div>
          {!isPast && (
            <button
              onClick={() => onAddSlot(day.date)}
              disabled={addingSlot}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:opacity-80 disabled:opacity-50"
            >
              {addingSlot ? <Spinner className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              Add Fit Slot
            </button>
          )}
        </div>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={saveTitle}
          placeholder="Add a title for this day…"
          className="mt-1 w-full bg-transparent rounded px-1 -mx-1 py-0.5 text-xs font-medium text-foreground placeholder:font-normal placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      {day.fits.length === 0 ? (
        <p className="text-xs text-muted-foreground">No fit slots yet.</p>
      ) : (
        <div className="space-y-2">
          {day.fits.map((fit, i) => (
            <SlotRow
              key={fit.planFitId}
              slot={fit}
              date={day.date}
              isPast={isPast}
              sessionName={`${planName} - ${dayLabel} - Fit ${i + 1}`}
              contextPrefix={`${tripLine} ${dayLine}`}
              onSelectFit={() => onSelectFit(fit)}
              onClearFit={() => onClearFit(fit)}
              onRemoveSlot={() => onRemoveSlot(fit)}
              onConfirm={() => onConfirm(fit.planFitId)}
              confirming={confirmingId === fit.planFitId}
              onSaveCaption={onSaveCaption}
              onAiPick={onAiPick}
              onSendToStylist={onSendToStylist}
              onExpandPhoto={onExpandPhoto}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function SlotRow({
  slot,
  date,
  isPast,
  sessionName,
  contextPrefix,
  onSelectFit,
  onClearFit,
  onRemoveSlot,
  onConfirm,
  confirming,
  onSaveCaption,
  onAiPick,
  onSendToStylist,
  onExpandPhoto,
}: {
  slot: PlannedFit;
  date: string;
  isPast: boolean;
  sessionName: string;
  contextPrefix: string;
  onSelectFit: () => void;
  onClearFit: () => void | Promise<void>;
  onRemoveSlot: () => void;
  onConfirm: () => void;
  confirming: boolean;
  onSaveCaption: (planFitId: string, caption: string) => void | Promise<void>;
  onAiPick: (slot: PlannedFit, date: string, caption: string) => Promise<string>;
  onSendToStylist: (planFitId: string, sessionName: string, blurb: string) => void | Promise<void>;
  onExpandPhoto: (fitId: string) => void;
}) {
  const hasFit = slot.fitId !== null;
  const confirmed = slot.confirmedWearId !== null;
  const [caption, setCaption] = useState(slot.caption ?? '');
  const [picking, setPicking] = useState(false);
  const [pickNote, setPickNote] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const captionReady = caption.trim() !== '';

  const saveCaption = async () => {
    if ((slot.caption ?? '') !== caption) await onSaveCaption(slot.planFitId, caption);
  };

  const aiPick = async () => {
    if (!captionReady || picking) return;
    setPicking(true);
    setPickNote(null);
    try {
      await saveCaption();
      const reason = await onAiPick(slot, date, caption.trim());
      setPickNote(reason || null);
    } catch (err) {
      setPickNote(err instanceof Error ? err.message : 'AI pick failed.');
    } finally {
      setPicking(false);
    }
  };

  const sendToStylist = async () => {
    if (!captionReady || sending) return;
    setSending(true);
    try {
      await saveCaption();
      await onSendToStylist(slot.planFitId, sessionName, `${contextPrefix} ${caption.trim()}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={cn(
        'rounded-xl border overflow-hidden',
        slot.hasWarning ? 'border-amber-300' : 'border-border',
        confirmed && 'ring-2 ring-primary',
      )}
    >
      <div className="flex items-center gap-2 p-3 pb-0">
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onBlur={saveCaption}
          placeholder="What's this fit for? (e.g. dinner on the beach)"
          className="flex-1 min-w-0 bg-secondary/50 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {!isPast && (
          <button
            onClick={onRemoveSlot}
            className="p-1.5 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
            aria-label="Remove slot"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {hasFit ? (
        <div className="flex items-center gap-3 p-3">
          <button
            type="button"
            onClick={() => onExpandPhoto(slot.fitId!)}
            title="View photos"
            aria-label="View fit photos"
            className="flex-shrink-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <FitThumb
              url={slot.imageUrl}
              piecePhotoUrls={slot.piecePhotoUrls}
              alt={slot.fitName ?? 'Fit'}
              className="w-14 h-14 rounded-lg"
            />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Link
                to={`/fits/${slot.fitId}`}
                className="flex items-center gap-0.5 min-w-0 text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                <span className="truncate">{slot.fitName}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </Link>
              {confirmed && (
                <Badge tone="success" className="flex-shrink-0">
                  <Check className="w-3 h-3" />
                  Worn
                </Badge>
              )}
            </div>
            {slot.hasWarning && slot.warningReasons.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {slot.warningReasons.map((reason, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[11px] text-amber-700 leading-tight">{reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {!confirmed && (
            <button
              onClick={onClearFit}
              title="Clear this fit (keep the slot)"
              aria-label="Clear fit"
              className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="p-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            <SlotAction icon={Search} label="Choose Fit" onClick={onSelectFit} />
            <SlotAction
              icon={Sparkles}
              label="AI Pick"
              busy={picking}
              disabled={!captionReady}
              title={captionReady ? 'Have the AI pick a fit from your library for this caption' : 'Write a caption first'}
              onClick={aiPick}
            />
            <SlotAction
              icon={Wand2}
              label="AI Build"
              busy={sending}
              disabled={!captionReady}
              title={captionReady ? 'Build a new fit with the AI stylist from this caption' : 'Write a caption first'}
              onClick={sendToStylist}
            />
          </div>
          {pickNote && <p className="text-[11px] text-muted-foreground leading-tight">AI: {pickNote}</p>}
        </div>
      )}

      {isPast && hasFit && !confirmed && (
        <button
          onClick={onConfirm}
          disabled={confirming}
          className="w-full py-2 bg-amber-50 border-t border-amber-200 text-xs font-medium text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
        >
          {confirming ? 'Confirming…' : 'Confirm Outfit (log wear)'}
        </button>
      )}
    </div>
  );
}

function SlotAction({
  icon: Icon,
  label,
  onClick,
  disabled,
  busy,
  title,
}: {
  icon: typeof Search;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      title={title}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground hover:border-primary/30 hover:text-primary transition-colors disabled:opacity-40 disabled:hover:border-border disabled:hover:text-foreground"
    >
      {busy ? <Spinner className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

// Everything to pack: unique pieces across the planned fits, by category.
function PackingList({ items }: { items: PackingItem[] }) {
  const byCategory: { category: string; items: PackingItem[] }[] = [];
  for (const item of items) {
    const group = byCategory.find((g) => g.category === item.category);
    if (group) group.items.push(item);
    else byCategory.push({ category: item.category, items: [item] });
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Luggage className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Packing List</h3>
        <span className="text-xs text-muted-foreground">
          {items.length} piece{items.length !== 1 ? 's' : ''}
        </span>
      </div>
      <Card className="divide-y divide-border">
        {byCategory.map((group) => (
          <div key={group.category} className="p-3 space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {group.category} · {group.items.length}
            </p>
            {group.items.map((item) => (
              <Link
                key={item.itemId}
                to={`/wardrobe/item/${item.itemId}`}
                className="flex items-center gap-3 rounded-lg -mx-1 px-1 py-1 hover:bg-secondary/40 transition-colors"
              >
                {item.photoUrl ? (
                  <img src={item.photoUrl} alt={item.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-secondary flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{item.name}</p>
                  {(item.brand || item.color) && (
                    <p className="text-xs text-muted-foreground truncate">
                      {[item.brand, item.color].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                {item.fitCount > 1 && (
                  <Badge tone="neutral" className="flex-shrink-0">
                    ×{item.fitCount} fits
                  </Badge>
                )}
                {item.status !== 'in-closet' && (
                  <Badge tone="warning" className="flex-shrink-0">
                    {STATUS_LABELS[item.status] ?? item.status}
                  </Badge>
                )}
              </Link>
            ))}
          </div>
        ))}
      </Card>
    </section>
  );
}

function EditPlanModal({
  isOpen,
  onClose,
  plan,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  plan: FitPlan;
  onSaved: () => void;
}) {
  const [name, setName] = useState(plan.name);
  const [planType, setPlanType] = useState<PlanType>(plan.planType);
  const [startDate, setStartDate] = useState(plan.startDate.slice(0, 10));
  const [endDate, setEndDate] = useState(plan.endDate.slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const datesValid = startDate !== '' && endDate !== '' && startDate <= endDate;
  const canSave = name.trim() !== '' && datesValid && !loading;

  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    setError(null);
    try {
      await api.patch(`/plans/${plan.id}`, {
        name: name.trim(),
        planType,
        startDate,
        endDate,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Plan"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button fullWidth onClick={handleSave} disabled={!canSave}>
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Field label="Plan Name">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
        </Field>
        <Field label="Plan Type">
          <select
            value={planType}
            onChange={(e) => setPlanType(e.target.value as PlanType)}
            className={inputClass}
          >
            {PLAN_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Start Date">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`${inputClass} w-full min-w-0`} />
          </Field>
          <Field label="End Date">
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={`${inputClass} w-full min-w-0`}
            />
          </Field>
        </div>
        {!datesValid && <p className="text-xs text-destructive">End date must be on or after the start date.</p>}
      </div>
    </Modal>
  );
}
